import React from 'react';
import _ from 'lodash';
import { func, object, string } from 'prop-types';
import styles from './EditDish.scss';
import {
  getDisplayableDishFromOrderItem,
  findByClientSideId,
  getAllVariationClientSideIds,
} from '../../utils/DishUtils';
import Quantity from '../Quantity';
import Variation from './Variation';
import ModalLayout from '../ModalLayout';
import { scroller } from 'react-scroll';
import { translate } from 'react-i18next';
import { getIcon } from '@wix/restaurants-menus-icons';
import { tmpLabels } from '../MenuItem/MenuItem';
import Banner from '../Banner';
import { getAvailabilityParsedByDaysAndHours } from '../../utils/TimeUtils';
import { AvailabilityTimesBanner } from '../AvailabilityTimesBanner/AvailabilityTimesBanner';

import { Text, Button, InputArea } from 'wix-style-react';

const EDIT_DISH_CONTAINER_ID = 'edit-dish-container-id';

export class EditDish extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = EditDish.init(props);
  }

  static init(props) {
    const displayableDish = EditDish.getDerivedDisplayableDish(props);
    const errorIndicationOverrides = EditDish.initErrorIndicationOverrides(displayableDish);

    return {
      displayableDish,
      displayErrorIndication: false,
      errorIndicationOverrides,
      dirty: [],
      wasValidAtSomePoint: [],
    };
  }

  static getDerivedStateFromProps(nextProps, currentState) {
    const displayableDish = EditDish.getDerivedDisplayableDish(nextProps);
    const { errorIndicationOverrides } = currentState;
    const wasValidAtSomePoint = _.cloneDeep(currentState.wasValidAtSomePoint);
    const clientSideIds = getAllVariationClientSideIds(displayableDish) || [];

    clientSideIds.forEach(clientSideId => {
      const { isValid } = findByClientSideId(displayableDish, clientSideId);
      if (isValid || wasValidAtSomePoint.includes(clientSideId)) {
        errorIndicationOverrides[clientSideId] = 'hidden';
        wasValidAtSomePoint.push(clientSideId);
      } else if (!_.isUndefined(errorIndicationOverrides[clientSideId])) {
        errorIndicationOverrides[clientSideId] = 'visible';
      }
    });

    return {
      displayableDish,
      errorIndicationOverrides,
      wasValidAtSomePoint: _.uniq(wasValidAtSomePoint),
    };
  }

  static initErrorIndicationOverrides(displayableDish) {
    return _(getAllVariationClientSideIds(displayableDish))
      .keyBy()
      .mapValues(() => 'visible')
      .value();
  }

  static getScrollingOffset() {
    try {
      return -1 * document.getElementById(EDIT_DISH_CONTAINER_ID).getBoundingClientRect().top;
    } catch (e) {
      return 0;
    }
  }

  static getDerivedDisplayableDish(props) {
    const { orderItem, itemsHash, locale, currency, deliveryType, simplifiedMenu } = props;
    return getDisplayableDishFromOrderItem({
      orderItem,
      itemsHash,
      locale,
      currency,
      deliveryType,
      flatten: false,
      simplifiedMenu,
    });
  }

  renderSubTitle(part) {
    const subTitle = (
      <Text
        size="medium"
        weight="normal"
        secondary
        data-hook="edit-dish-description"
        className={styles.description}
        key="sub-title"
      >
        {part.value}
      </Text>
    );

    return part.value ? subTitle : null;
  }

  renderVariation(part, disabled) {
    const { displayErrorIndication } = this.state;
    const { errorIndicationOverrides } = this.state;

    return (
      <Variation
        key={part.clientSideId}
        part={part}
        onChange={this.handleChoiceChange}
        displayErrorIndication={displayErrorIndication}
        errorIndicationOverrides={errorIndicationOverrides}
        disabled={disabled}
      />
    );
  }

  handleSubmitButtonClick = (isDisabled, highlightedError) => {
    const { orderItem, onSubmit } = this.props;
    const { displayableDish } = this.state;

    this.setState({
      displayErrorIndication: isDisabled,
      errorIndicationOverrides: EditDish.initErrorIndicationOverrides(displayableDish),
      wasValidAtSomePoint: [],
    });

    if (!isDisabled) {
      onSubmit(orderItem);
    } else {
      const options = {
        containerId: EDIT_DISH_CONTAINER_ID,
        smooth: 'easeOutCubic',
        duration: 500,
        offset: EditDish.getScrollingOffset(),
      };
      scroller.scrollTo(highlightedError, options);
    }
  };

  renderFooter = (submit, disabled, quantity) => {
    const { t } = this.props;
    const amountText = submit.value;
    const amount = amountText ? `: ${amountText}` : '';
    return (
      <div key="submit" className={styles.submit}>
        <Quantity
          disabled={disabled}
          value={quantity.value}
          min={1}
          onUpClick={() => this.handleUpDownClick(_.add)}
          onDownClick={() => this.handleUpDownClick(_.subtract)}
        />

        <Button
          disabled={disabled}
          dataHook="edit-dish-submit-button"
          onClick={() => this.handleSubmitButtonClick(submit.disabled, submit.highlightedError)}
        >
          {t('phoneordering_menu_dish_add_cta', { amount })}
        </Button>
      </div>
    );
  };

  handleChoiceChange = (variationPath, choiceId, clientSideId) => {
    const { toggleChoice } = this.props;
    const dirty = [...this.state.dirty, clientSideId];

    this.setState({ dirty }, () => {
      toggleChoice({ variationPath, choiceId });
    });
  };

  handleUpDownClick(operator) {
    const { orderItem, changeCount } = this.props;
    const count = operator(_.isNumber(orderItem.count) ? orderItem.count : 1, 1);
    changeCount({ count });
  }

  handleSpecialRequestChange = e => {
    const { changeSpecialRequest } = this.props;

    const specialRequestValue = e.target.value;
    changeSpecialRequest({ specialRequestValue });
  };

  renderErrors(errors) {
    const availabilityPeriods = this.props.orderItem.availability
      ? getAvailabilityParsedByDaysAndHours(this.props.orderItem.availability)
      : [];

    const { type, supportedDeliveryTypes } = _.first(errors);
    if (type === 'error.soldout') {
      return (
        <Text
          size="medium"
          weight="normal"
          secondary
          className={styles.soldOut}
          data-hook="edit-dish-error"
          tagName="div"
          skin="error"
        >
          Sold out
        </Text>
      );
    } else if (type === 'error.unavailable') {
      return (
        <React.Fragment>
          <Text
            size="medium"
            weight="normal"
            secondary
            className={styles.unavailable}
            data-hook="edit-dish-error"
            tagName="div"
            skin="error"
          >
            Unavailable
          </Text>
          {availabilityPeriods.length > 0 && (
            <AvailabilityTimesBanner availability={availabilityPeriods} title={'Dish will be available at:'} />
          )}
        </React.Fragment>
      );
    } else if (type === 'error.deliveryType') {
      return (
        <React.Fragment>
          <Text
            size="medium"
            weight="normal"
            secondary
            className={styles.unavailable}
            data-hook="edit-dish-error"
            tagName="div"
            skin="error"
          >
            Unavailable
          </Text>
          <Banner data-hook="edit-dish-error-details" className={styles.errorDetails}>
            This dish is available for {supportedDeliveryTypes.map(s => s).join(',')} only.
          </Banner>
        </React.Fragment>
      );
    }
  }

  renderLabels() {
    const { orderItem: item } = this.props;
    const labels = (item.labels || []).filter(label => label !== 'featured');

    return labels.length ? (
      <div className={styles.labels} data-hook="menu-item-labels">
        {labels.map(this._getLabel)}
      </div>
    ) : null;
  }

  _getLabel(label) {
    const Icon = getIcon('full-bg', label);

    return Icon ? (
      <div className={styles.label} key={label} title={tmpLabels[label]}>
        <Icon size={30} />
      </div>
    ) : null;
  }

  shouldAddMargin() {
    const labels = (this.props.orderItem.labels || []).filter(label => label !== 'featured');
    return this.state.displayableDish.variations.length > 0 && !_.isEmpty(labels);
  }

  render() {
    const { displayableDish } = this.state;
    const { onCloseClick, t } = this.props;
    const { title, subTitle, quantity, variations, specialRequest, submitButton, errors } = displayableDish;
    const hasErrors = !_.isEmpty(errors);
    return (
      <ModalLayout
        title={title.value}
        paddingBottom="12px"
        footer={this.renderFooter(submitButton, hasErrors, quantity)}
        onCloseClick={onCloseClick}
        contentElementId={EDIT_DISH_CONTAINER_ID}
      >
        <div className={styles.wrapper}>
          {hasErrors && this.renderErrors(errors)}
          {this.renderSubTitle(subTitle)}
          {this.renderLabels()}
          {variations.map(variation => this.renderVariation(variation, hasErrors))}
          <div className={styles.specialRequestWrapper} data-hook="edit-dish-special-request-wrapper">
            <Text size="medium" weight="thin" className={styles.specialRequestTitle}>
              {t('phoneordering_checkout_requests_label')}
            </Text>
            <div className={styles.inputArea}>
              <InputArea
                autoGrow
                placeholder={t('phoneordering_checkout_requests_placeholder')}
                onChange={this.handleSpecialRequestChange}
                value={specialRequest.value}
                dataHook="edit-dish-special-request-input"
              />
            </div>
          </div>
        </div>
      </ModalLayout>
    );
  }
}

EditDish.propTypes = {
  orderItem: object.isRequired,
  onSubmit: func.isRequired,
  onCloseClick: func.isRequired,
  itemsHash: object.isRequired,
  locale: string.isRequired,
  currency: string.isRequired,
  changeSpecialRequest: func.isRequired,
  t: func.isRequired,
};

export default translate()(EditDish);
